import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Link } from 'react-router-dom';

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  );
}

const socialLinks = [
  { label: 'Instagram', href: 'https://www.instagram.com/fasttaskagency/', icon: InstagramIcon },
];

const footerLinks = [
  { label: 'Mentions légales', href: '/mentions-legales' },
  { label: 'Politique de confidentialité', href: '/politique-confidentialite' },
  { label: 'CGV', href: '/cgv' },
];

export default function Footer() {
  const ref = useScrollReveal();

  return (
    <footer id="contact" data-testid="footer-section" className="py-16 md:py-20 border-t border-border/50">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div ref={ref} className="scroll-reveal">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-12 md:gap-10 mb-16">
            {/* Brand */}
            <div>
              <Link to="/" className="text-2xl font-bold tracking-tight text-foreground inline-block mb-4">
                Fast<span className="text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">Task</span>
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                Agence d'automatisation IA en France et en Tunisie pour restaurants, cabinets médicaux et commerces.
                Gagnez du temps, réduisez vos coûts, développez votre activité.
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4">Nos services</h4>
              <nav aria-label="Services FastTask" className="space-y-3 text-sm">
                <Link to="/services/restaurant" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Automatisation Restaurant
                </Link>
                <Link to="/services/medical" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Automatisation Médical
                </Link>
                <Link to="/services/retail" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Automatisation Commerce
                </Link>
              </nav>
            </div>

            {/* Agences */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4">Nos agences</h4>
              <nav aria-label="Agences FastTask" className="space-y-3 text-sm">
                <Link to="/agence-automatisation-ia-france" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Agence automatisation IA France
                </Link>
                <Link to="/agence-automatisation-ia-tunisie" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Agence automatisation IA Tunisie
                </Link>
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4">Contact</h4>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p data-testid="footer-email">contact@fasttask.agency</p>
                <p data-testid="footer-phone">+216 29 275 042</p>
                <p data-testid="footer-phone-2">+216 24 112 051</p>
                <p data-testid="footer-address-tn">La Marsa, Tunisie</p>
              </div>
            </div>

            {/* Social */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4">Suivez-nous</h4>
              <div className="flex gap-4">
                {socialLinks.map(link => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-link-${link.label.toLowerCase().replace(/[^a-z]/g, '')}`}
                      className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all duration-200"
                      aria-label={link.label}
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="section-divider mb-8" />
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} Fast Task. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              {footerLinks.map(link => (
                <Link
                  key={link.label}
                  to={link.href}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="footer-link text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
